import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useFetchCategories from '../../../hooks/useFetchCategories';
import handleKeyboard from '../../../hooks/handleKeyboardInput';
import SearchHistory from '../../../hooks/interactsWithSearchHistory';
import { useMatchesMinWidth } from '../../../hooks/useMatchesMinWidth';
import { BREAKPOINTS } from '../../../../constants';
import qs from 'qs';
import pages from '../../../define/pages';
import cn from 'classnames';

import SearchBar, {
  SearchBarVariants,
} from '../../../ui-components/SearchBar/SearchBar';
import SmartSearchDropdown from './SmartSearchDropdown';
import SmartSearchNoResults from './SmartSearchNoResults';
import SmartSearchGeoLocator from './SmartSearchGeolocator';
import SmartSearchHistory from './SmartSearchHistory';
import SmartSearchBillboards from './SmartSearchBillboards';
import SmartSearchHeader from './SmartSearchHeader';
import Skeleton from '../../../ui-components/Skeleton/Skeleton';

export default function SmartSearchContent({
  title,
  isLoading,
  hasFetched,
  error,
  searchValue,
  setSearchValue,
  suggestions,
}) {
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollBoxRef = useRef(null);

  const popularCategories = useFetchCategories();
  const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);

  const isSearchValueEmpty = searchValue === '';
  const isSuggestionsEmpty = suggestions.length === 0;

  const showContents = !isLoading && !error && isSearchValueEmpty;
  const showSuggestionSkeleton = !hasFetched && !error && !isSearchValueEmpty;
  const showSuggestions = !error && !isSearchValueEmpty && !isSuggestionsEmpty;
  const showNoResultsMessage =
    // !isLoading &&
    !error && !isSearchValueEmpty && hasFetched && isSuggestionsEmpty;
  // const showErrorMessage = error && !isSearchValueEmpty;

  // TODO: This should be a reusable hook as it can be used elsewhere
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const position = Math.ceil(
        (scrollTop / (scrollHeight - clientHeight)) * 100
      );
      setScrollPosition(position);
    },
    [setScrollPosition]
  );

  const handleOnChange = (value) => {
    setSearchValue(value);
    setSelectedSuggestionIndex(-1);
    scrollBoxRef.current?.scrollTo(0, 0);
  };

  const handleSearch = useCallback(() => {
    const searchDepth = suggestions.length ?? 0;
    if (isSuggestionsEmpty || isSearchValueEmpty) return;
    for (let i = 0; i < searchDepth; i++) {
      let suggestion = suggestions[i];

      if (
        searchValue?.localeCompare(suggestion?.title, undefined, {
          sensitivity: 'base',
        }) == 0 ||
        searchValue?.localeCompare(suggestion?.search_term, undefined, {
          sensitivity: 'base',
        }) == 0
      ) {
        SearchHistory.append({
          title: suggestion.title,
          url: suggestion.link,
        });
        dataLayer.push({
          query: suggestion.title,
          suggestions: [],
          event: 'searchUserMatchedSuggestion',
        });
        window.location.href = suggestion.link;
        return;
      }
    }

    if (!isSearchValueEmpty) {
      const redirectParams = qs.stringify({
        search: searchValue,
      });
      const redirectUrl = pages.catalogue + `?${redirectParams}`;
      SearchHistory.append({ title: searchValue, url: redirectUrl });
      dataLayer.push({
        query: searchValue,
        suggestions: [],
        event: 'searchUserFreeQuery',
      });
      window.location.href = redirectUrl;
    }
  }, [
    searchValue,
    suggestions,
    selectedSuggestionIndex,
    isSuggestionsEmpty,
    isSearchValueEmpty,
    SearchHistory,
  ]);

  // TODO: This should be a reusable hook as it can be used elsewhere
  const keyboardHandler = (e) =>
    handleKeyboard({
      e,
      data: selectedSuggestionIndex,
      arrowUp: (e, selectedIndex) => {
        if (isSuggestionsEmpty) return;
        selectedIndex ??= suggestions.length - 1;
        selectedIndex - 1 < 0
          ? setSelectedSuggestionIndex(suggestions.length - 1)
          : setSelectedSuggestionIndex(selectedIndex - 1);
      },
      arrowDown: (e, selectedIndex) => {
        if (isSuggestionsEmpty) return;
        selectedIndex ??= 0;
        selectedIndex + 1 > suggestions.length
          ? setSelectedSuggestionIndex(0)
          : setSelectedSuggestionIndex(selectedIndex + 1);
      },
      // eslint-disable-next-line no-unused-vars
      enter: (e, selectedIndex) => {
        if (isSuggestionsEmpty) return;
        selectedIndex ??= 0;
        handleSearch();
      },
    });

  const handleNewSearch = useCallback(() => {
    setSearchValue('');
    // TODO: The searchBar should be responsible for this behaviour
    const searchBarElements = document.getElementsByClassName('search-bar');
    const lastItem = searchBarElements.length - 1;
    searchBarElements[lastItem].firstElementChild.focus();
  }, [setSearchValue]);

  const suggestionSkeletonList = (
    <div
      className={cn('d-flex flex-column gap-3xs', {
        'pl-xs pb-xs pr-xs': isDeviceSmallerThanTablet,
        'pl-l pb-l pr-l': !isDeviceSmallerThanTablet,
      })}
    >
      {[...Array(15)].map((element, index) => {
        const skeletonWidth = [150, 180, 200][index % 3];

        return (
          <div
            key={index}
            className={cn('d-flex gap-3xs items-center', {
              'h-xl': isDeviceSmallerThanTablet,
              'h-3xl': !isDeviceSmallerThanTablet,
            })}
          >
            <Skeleton classNames="radius-rounded" width={48} height={48} />
            <Skeleton classNames="radius-s" width={skeletonWidth} height={24} />
          </div>
        )
      }
      )}
    </div>
  );

  return (
    <div className="smart-search__container">
      {!isDeviceSmallerThanTablet && <SmartSearchHeader title={title} />}
      <div className="smart-search__container--search">
        <SearchBar
          variant={SearchBarVariants.primary}
          value={searchValue}
          handleOnChange={(e) => handleOnChange(e.target.value)}
          keyboardHandler={keyboardHandler}
          handleSearch={handleSearch}
          isAutoFocused
        />
      </div>
      <div
        className={`smart-search__container--body ${scrollPosition > 10 && 'scroll-offset'
          }`}
      >
        {showSuggestionSkeleton && <>{suggestionSkeletonList}</>}
        <div
          className="smart-search__container--body--scrollbox"
          onScroll={handleScroll}
          ref={scrollBoxRef}
        >
          {showNoResultsMessage && (
            <SmartSearchNoResults
              searchValue={searchValue}
              handleNewSearch={handleNewSearch}
              popularCategories={popularCategories}
            />
          )}
          {/* {showErrorMessage && (
              <p>Ooops qualcosa e' andato storto, riprova una ricerca</p>
            )} */}
          {showSuggestions && (
            <SmartSearchDropdown
              content={suggestions}
              isOpen={true}
              searchValue={searchValue}
              setSearchValue={(value) => {
                dataLayer.push({
                  query: value,
                  suggestions: [],
                  event: 'searchUserClickedSuggestion',
                });
                setSearchValue(value);
              }}
            />
          )}
          {showContents && (
            <>
              <div className="smart-search__container--body--user-context">
                <SmartSearchGeoLocator />
              </div>
              <div className="smart-search__container--body--previous-searches">
                <SmartSearchHistory setSearchValue={setSearchValue} />
              </div>
              <div className="smart-search__container--body--featured">
                {isDeviceSmallerThanTablet ? (
                  <SmartSearchBillboards
                    carouselWrapped={true}
                    popularCategories={popularCategories}
                  />
                ) : (
                  <SmartSearchBillboards
                    popularCategories={popularCategories}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

SmartSearchContent.propTypes = {
  title: PropTypes.string,
  suggestions: PropTypes.array,
  isLoading: PropTypes.bool,
  hasFetched: PropTypes.bool,
  error: PropTypes.any,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
};
